import { memo, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { TextStrikethrough } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import type { BlockBodyAnchor } from '../blockBodyAnchor';
import { getString } from '@/utils/chatUtils/chatDefaultUtils';
import { selectTodayStudyHistoryData } from '@/slices/todayStudyHistory/todayStudyHistorySlice';
import { useEnsureTodayStudyHistory } from '@/hooks/useEnsureTodayStudyHistory';
import { selectCurrentUser } from '@/slices/user/userSlice';
import { useAccess } from '@/hooks/useAccess';
import { submitChatVignetteAnswer } from '@/services/vignettes';
import {
  hasVignetteContent,
  renderVignetteContent,
} from '@/utils/vignetteHtml';
import { replaceVignetteImage } from '@/utils/vignetteImageParser';
import UpgradeModal from '@/components/ora/Upgrade/UpgradeModal';
import {
  MissedQuestionBadgeModals,
  MissedQuestionBadgeRow,
} from './MissedQuestionBadges';
import {
  isAnsweredCorrectly,
  readServedMarks,
  resolveQuestionOptions,
} from './questionItem';
import { useChatQuestionMarks } from './useChatQuestionMarks';
import type { QuestionBadgeAddResult } from './questionBadgeAddResult';
import { useMissedQuestionBadges } from './useMissedQuestionBadges';
import { useOptionCrossoutGestures } from './useOptionCrossoutGestures';
import { useScopedStemHighlighting } from './useScopedStemHighlighting';

// Images in vignette copy are licensed material; render them through the same
// handler QBank uses so the caption and credit line come with them.
const vignetteParserOptions = { replace: replaceVignetteImage };

/**
 * The stem's markup. Memoized on the markup alone because the highlighter
 * edits the rendered stem in place (ORA-820): a re-render of the block must
 * never reconcile over those edits.
 */
const QuestionStemContent = memo(({ html }: { html: string }) => (
  <>{renderVignetteContent(html, vignetteParserOptions)}</>
));

QuestionStemContent.displayName = 'QuestionStemContent';

type QuestionBlockBodyProps = {
  anchor?: BlockBodyAnchor;
  item?: Record<string, unknown>;
  onAnchorChange: (anchor: BlockBodyAnchor) => void;
  /** Preserve the surrounding scroll position before revealing the answer. */
  onBeforeContentExpand?: () => void;
  /** Called only after the grading write succeeds, never for optimistic reveal. */
  onAnswerConfirmed?: (firstAttemptCorrect: boolean) => void;
  // The lecture a document chat is grounded in; threaded to the answer submit so
  // the response is attributed to that lecture's exam.
  lectureId?: string;
  // The study set this question was served in (ORA-677); threaded to the answer
  // submit so the set's item is marked done by answering it here.
  studySetId?: string;
  // The block the question belongs to (a course QBANK step); threaded to the
  // answer submit so the response is recorded in that block.
  blockId?: string;
  // Opens a flashcards tool block from the linked-flashcard badge with the
  // supplied cards. Only the legacy chat panel passed it, and that panel is gone
  // (ORA-455): no host passes it now.
  onOpenLinkedFlashcards?: (cards: Array<Record<string, unknown>>) => void;
  // Companion only (ORA-546): adds the cards to the conversation as a flashcards
  // block or a flashcard study set, server-side, instead of opening a local tool
  // block. Takes precedence over `onOpenLinkedFlashcards`, and is run from the
  // curation modal's "Study Now" (ORA-832) with the cards left enabled there.
  onAddLinkedFlashcardsBlock?: (
    flashcardIds: string[]
  ) => Promise<QuestionBadgeAddResult>;
  // What studying those cards will produce, when it is not the rateable deck the
  // modal's copy would otherwise imply (ORA-832). A single served question has
  // no set to mint a review set from, so its cards arrive as a flip-through
  // block; the modal says so rather than promising the deck.
  linkedFlashcardsStudyNote?: string;
  // Companion only (ORA-547): studies the scheduled variant in the conversation,
  // from the preview modal's "Study Now" (ORA-831). Without it the modal is
  // preview-only, which is all the exam screen can do with a variant.
  onAddScheduledVariantBlock?: (
    variantId: string
  ) => Promise<QuestionBadgeAddResult>;
  // Why those two cannot run yet, when the Companion knows they cannot — the
  // answer the block would hang off is still being written. Both badges say so
  // and do nothing, rather than falling back to a preview or to silence.
  addBlocksUnavailableReason?: string;
  // Draw the "Scheduled 1 QBank Question" / "Assigned N Linked Flashcards"
  // badges after a miss. Off unless the host says otherwise: the exam screen
  // draws its own row (`SRBadge`), and a badge is only worth showing where
  // there is a way to study what it points at (ORA-696, answered by ORA-831).
  // The chat passes it for a question the student may still act on.
  showStudyBadges?: boolean;
  // Whether the linked-flashcard half of the badge row may be drawn at all.
  // Separate from `showStudyBadges` because that half carries QBank's own two
  // gates — the student's exam has flashcards switched on, and a preclinical
  // standalone question never offers them (`SRBadge.tsx`) — and neither says
  // anything about the scheduled-question half. Off suppresses the fetch too.
  allowLinkedFlashcards?: boolean;
};

export const QuestionBlockBody = ({
  anchor,
  item,
  onAnchorChange,
  onBeforeContentExpand,
  onAnswerConfirmed,
  lectureId,
  studySetId,
  blockId,
  onOpenLinkedFlashcards,
  onAddLinkedFlashcardsBlock,
  linkedFlashcardsStudyNote,
  onAddScheduledVariantBlock,
  addBlocksUnavailableReason,
  showStudyBadges = false,
  allowLinkedFlashcards = true,
}: QuestionBlockBodyProps) => {
  const vignetteAnchor = anchor?.vignette as
    Record<string, unknown> | undefined;

  const [selected, setSelected] = useState(
    getString(vignetteAnchor?.user_selected_letter)
  );

  const [hasSubmitError, setHasSubmitError] = useState(false);
  const [upgradeModalIsOpen, setUpgradeModalIsOpen] = useState(false);

  // Whether the answer is a real graded submission (not the in-flight optimistic
  // reveal): true once the answer write succeeds, or on load when the anchor
  // already carries the user's selected letter from a prior session. Gates the
  // linked-flashcard fetch so it runs against scheduling the backend has actually
  // written, not the optimistic reveal that precedes the answer write.
  const [answerConfirmed, setAnswerConfirmed] = useState(
    Boolean(getString(vignetteAnchor?.user_selected_letter))
  );

  const reportedAnswer = useRef<string | null>(null);
  const todayStudyHistory = useSelector(selectTodayStudyHistoryData);
  useEnsureTodayStudyHistory();
  const currentUser = useSelector(selectCurrentUser);
  const { isGated, limits } = useAccess();
  const vignetteItemId = getString(item?.id);
  // Shared by the render, the linked-flashcard fetch, and the first-answer
  // report so all three agree on correctness.
  const options = resolveQuestionOptions(item);

  const questionStem =
    getString(item?.question_stem) ||
    getString(item?.key_point) ||
    'Question data is missing for this search result.';

  // Highlights and crossouts (ORA-820), as QBank offers them: open until the
  // question is answered, then kept on screen but frozen.
  const marksEnabled = !selected;
  const stemRef = useRef<HTMLDivElement>(null);
  const optionsRef = useRef<HTMLDivElement>(null);

  const marks = useChatQuestionMarks({
    vignetteId: vignetteItemId,
    variantId: getString(item?.variant_id),
    stemHtml: questionStem,
    optionLetters: options.map((option) => option.letter),
    served: readServedMarks(item),
    userId: currentUser?.id,
  });

  useScopedStemHighlighting(stemRef, {
    disabled: !marksEnabled,
    onChange: marks.onHighlightsChange,
    scopeKey: marks.marksKey,
  });

  const { onOptionContextMenu, isLongPressClick } = useOptionCrossoutGestures(
    optionsRef,
    {
      disabled: !marksEnabled,
      onToggle: marks.toggleCrossout,
      scopeKey: marks.marksKey,
    }
  );

  // The student's QBank preference for the per-option strikeout icon; the
  // right-click and long-press gestures work either way, as they do there.
  const showCrossoutToggle =
    marksEnabled && (currentUser?.qbankShowStrikeoutIcon ?? true);

  const badges = useMissedQuestionBadges({
    vignetteAnchor,
    vignetteItemId,
    selected,
    answerConfirmed,
    options,
    timezone: currentUser?.timezone,
    showStudyBadges,
    allowLinkedFlashcards,
    addBlocksUnavailableReason,
    onOpenLinkedFlashcards,
    onAddLinkedFlashcardsBlock,
    onAddScheduledVariantBlock,
  });

  const { setScheduledVariant } = badges;

  // A restored selected letter is already a persisted answer, and a newly
  // submitted one becomes eligible only after its write succeeds. Report each
  // answer once; an optimistic reveal that rolls back never reaches this path.
  useEffect(() => {
    if (!answerConfirmed || !selected || reportedAnswer.current === selected)
      return;

    reportedAnswer.current = selected;
    onAnswerConfirmed?.(isAnsweredCorrectly(options, selected));
  }, [answerConfirmed, onAnswerConfirmed, options, selected]);

  if (!item) return <div>No more new questions in this turn.</div>;

  const correctLetter = getString(item.correct_letter);
  const correctExplanation = getString(item.correct_explanation);

  // Post-reveal we show every option's explanation, not just the correct one —
  // the same treatment QBank gives, correct answer hoisted to the top. An
  // option with nothing to say is dropped, except the correct one: "why B is
  // right" is the whole point of the panel, so it falls back to the
  // question-level explanation rather than disappearing. If no option has an
  // explanation at all — turns persisted before the backend sent per-option
  // ones — the single correct-answer explanation is rendered below instead.
  const explainedOptions = [
    ...options.filter((option) => option.isCorrect),
    ...options.filter((option) => !option.isCorrect),
  ]
    .map((option) => ({
      ...option,
      explanation: hasVignetteContent(option.explanation)
        ? option.explanation
        : option.isCorrect
          ? correctExplanation
          : '',
    }))
    .filter((option) => hasVignetteContent(option.explanation));

  const isAtDailyLimit =
    isGated() && todayStudyHistory.qbank.total >= limits.qbank;

  // Keyword explainers are answer-revealing: the popover for a term in the stem
  // or in an option often names the diagnosis outright. QBank keeps them inert
  // until the question is answered (`ExamView` sets the same two attributes on
  // its stem and answer block); chat had never opted in. `selected` is seeded
  // from the persisted anchor, so a question answered in an earlier session
  // stays open. The link attribute stays on permanently, as it does in QBank:
  // a keyword should never navigate the student out of the conversation.
  const explainersDisabled = selected ? undefined : 'true';

  const submitAnswer = async (letter: string) => {
    // Once answered the options are non-answerable, but they stay in the DOM as
    // real (non-`disabled`) buttons so keyword explainers inside them keep
    // receiving pointer events — see the `aria-disabled` note on the option
    // below. Guard here in lieu of the native `disabled` attribute.
    if (selected) return;

    if (isAtDailyLimit) {
      setUpgradeModalIsOpen(true);
      return;
    }

    // Optimistically reveal the answer immediately so clicking an option feels
    // instant; the answer write happens in the background and we only roll back
    // if it fails. `selected` is always empty here because a revealed question
    // disables its options, but we capture it to be explicit about the rollback.
    const previousSelected = selected;
    onBeforeContentExpand?.();
    setSelected(letter);
    setHasSubmitError(false);

    onAnchorChange({
      type: 'vignette',
      id: getString(item.id),
      vignette: {
        revealed: true,
        user_selected_letter: letter,
        variant_id: getString(item.variant_id) || null,
      },
    });

    const { status, scheduledVariant: nextScheduledVariant } =
      await submitChatVignetteAnswer({
        vignetteId: getString(item.id),
        vignetteVariantId: getString(item.variant_id) || undefined,
        userAnswer: letter,
        correctAnswer: correctLetter,
        lectureId,
        studySetId,
        blockId,
      });

    if (status === 'success') {
      // The backend schedules a variant when the question was missed; surface
      // it via the badge (rendered only when it is due in the future) and persist
      // it into the anchor so the badge survives a conversation reload.
      setScheduledVariant(nextScheduledVariant);

      // The answer is now a real graded submission, so the linked-flashcard
      // effect may fetch against the scheduling the backend just wrote.
      setAnswerConfirmed(true);

      onAnchorChange({
        type: 'vignette',
        id: getString(item.id),
        vignette: {
          revealed: true,
          user_selected_letter: letter,
          variant_id: getString(item.variant_id) || null,
          scheduled_variant: nextScheduledVariant,
        },
      });

      return;
    }

    // Roll back the optimistic reveal so the user can retry or upgrade.
    setSelected(previousSelected);

    onAnchorChange({
      type: 'vignette',
      id: getString(item.id),
      vignette: {
        revealed: false,
        user_selected_letter: previousSelected,
        variant_id: getString(item.variant_id) || null,
      },
    });

    if (status === 'limit_reached') {
      setUpgradeModalIsOpen(true);
    } else {
      setHasSubmitError(true);
    }
  };

  return (
    <div className="chat-ui_question">
      <div
        className="chat-ui_question-stem prose-ora prose-ora-study"
        data-explainer-link-disabled="true"
        data-explainers-disabled={explainersDisabled}
        key={marks.marksKey}
        ref={stemRef}
      >
        <QuestionStemContent html={marks.stemHtml} />
      </div>
      {!options.length ? (
        <div className="chat-ui_missing">
          No answer options were available for this question result.
          {getString(item.id) ? ` Result ID: ${getString(item.id)}.` : ''}
        </div>
      ) : null}
      {options.length ? (
        <div className="chat-ui_question-options" ref={optionsRef}>
          {options.map((option) => {
            const { letter } = option;
            const isSelected = selected === letter;
            const isRevealed = Boolean(selected);
            const isCorrectAnswer = isRevealed && option.isCorrect;

            const isIncorrectAttempt =
              isRevealed && isSelected && !option.isCorrect;

            const isCrossedOut = marks.isCrossedOut(letter);

            return (
              <div className="chat-ui_question-option-row" key={letter}>
                <button
                  className={`chat-ui_question-option${
                    isSelected ? ' is-selected' : ''
                  }${isIncorrectAttempt ? ' is-incorrect-attempt' : ''}${
                    isCorrectAnswer ? ' is-correct-answer' : ''
                  }${isCrossedOut ? ' crossed-out' : ''}${
                    showCrossoutToggle ? ' has-crossout-toggle' : ''
                  }`}
                  data-explainer-link-disabled="true"
                  data-explainers-disabled={explainersDisabled}
                  data-option-letter={letter}
                  // Not the native `disabled` attribute: a disabled <button> swallows
                  // mouse/pointer events for its whole subtree, which would stop the
                  // keyword explainers rendered inside the option text from ever
                  // opening (ExplainerPopoversManager listens on document-level
                  // mouseover/click). `aria-disabled` + the guard in submitAnswer keeps
                  // an answered option non-answerable while letting those events flow.
                  aria-disabled={Boolean(selected) || undefined}
                  onClick={() => {
                    // The click that ends a long-press crossout is not an answer.
                    if (isLongPressClick(letter)) return;
                    void submitAnswer(letter);
                  }}
                  onContextMenu={(event) => onOptionContextMenu(letter, event)}
                  type="button"
                >
                  <span className="chat-ui_question-option-label">
                    <strong>{letter}.</strong>
                    <span>
                      {renderVignetteContent(
                        option.text,
                        vignetteParserOptions
                      )}
                    </span>
                  </span>
                </button>
                {showCrossoutToggle ? (
                  <span className="chat-ui_question-crossout-toggle">
                    <Button
                      aria-label={`${isCrossedOut ? 'Remove' : 'Apply'} crossout on option ${letter}`}
                      aria-pressed={isCrossedOut}
                      onClick={() => marks.toggleCrossout(letter)}
                      size="icon-xs"
                      type="button"
                      variant="ghost"
                    >
                      <TextStrikethrough aria-hidden="true" />
                    </Button>
                  </span>
                ) : null}
              </div>
            );
          })}
        </div>
      ) : null}
      {hasSubmitError ? (
        <div className="chat-ui_missing">
          Your answer could not be submitted. Please try again.
        </div>
      ) : null}
      {selected ? (
        <div className="chat-ui_explanation">
          <strong>Correct: {correctLetter}</strong>
          {explainedOptions.length ? (
            explainedOptions.map((option) => (
              <div
                className={`chat-ui_explanation-answer${
                  option.isCorrect ? ' is-correct' : ''
                }${option.letter === selected ? ' user-answer' : ''}`}
                key={option.letter}
              >
                <p className="chat-ui_explanation-answer-option">
                  <strong>{option.letter}. </strong>
                  {renderVignetteContent(option.text, vignetteParserOptions)}
                </p>
                <div className="chat-ui_explanation-answer-body prose-ora prose-ora-study">
                  {renderVignetteContent(
                    option.explanation,
                    vignetteParserOptions
                  )}
                </div>
              </div>
            ))
          ) : hasVignetteContent(correctExplanation) ? (
            <div className="chat-ui_explanation-answer-body prose-ora prose-ora-study">
              {renderVignetteContent(correctExplanation, vignetteParserOptions)}
            </div>
          ) : null}
          <MissedQuestionBadgeRow badges={badges} />
        </div>
      ) : null}
      <MissedQuestionBadgeModals
        badges={badges}
        linkedFlashcardsStudyNote={linkedFlashcardsStudyNote}
        vignetteId={vignetteItemId}
      />
      <UpgradeModal
        open={upgradeModalIsOpen}
        close={() => setUpgradeModalIsOpen(false)}
        contentType="QBank"
      />
    </div>
  );
};
